import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Home, ArrowLeft, FileSignature, CheckCircle2, Clock, XCircle } from 'lucide-react';

/**
 * Tenant-facing progress timeline for an application/lease.
 * Stages: Applied → Landlord Review → Signed → Rent Paid
 */
/* eslint-disable react/prop-types */
function ApplicationStepper({ status, paymentStatus }) {
  // Determine current stage index.
  let current = 0; // Applied
  if (status === 'active') current = 2; // Landlord signed
  if (status === 'active' && paymentStatus === 'paid') current = 3; // Fully paid
  if (status === 'pending_landlord') current = 1; // Under review
  const rejected = status === 'rejected';
  const cancelled = status === 'cancelled';

  const stages = [
    { key: 'applied', label: 'Applied' },
    { key: 'review', label: 'Landlord Review' },
    { key: 'signed', label: 'Signed' },
    { key: 'paid', label: 'Rent Paid' },
  ];

  if (rejected || cancelled) {
    return (
      <div className="flex items-center gap-2 mt-4 text-sm">
        <XCircle className="w-4 h-4 text-red-600" />
        <span className="font-medium text-red-700">
          {rejected ? 'Rejected by landlord' : 'Application cancelled'}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="application-stepper">
      <div className="flex items-center gap-2">
        {stages.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return (
            <React.Fragment key={s.key}>
              <div className="flex items-center gap-2">
                <div
                  data-testid={`stepper-dot-${s.key}`}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                    done
                      ? 'bg-primary text-primary-foreground'
                      : active
                      ? 'bg-primary/20 text-primary border border-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                </div>
                <span className={`text-xs ${active ? 'font-semibold' : 'text-muted-foreground'}`}>{s.label}</span>
              </div>
              {i < stages.length - 1 && (
                <div className={`flex-1 h-px ${i < current ? 'bg-primary' : 'bg-border'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
/* eslint-enable react/prop-types */

export default function LeasesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLeases(); }, []);

  const fetchLeases = async () => {
    try {
      const response = await axios.get(`${API}/leases`);
      setLeases(response.data);
    } catch (error) {
      toast.error('Failed to fetch leases');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, payment_status) => {
    if (status === 'active' && payment_status === 'paid') return <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" />Active & Paid</span>;
    if (status === 'active') return <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><CheckCircle2 className="w-3 h-3" />Active - Payment Pending</span>;
    if (status === 'pending_landlord') return <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3" />Awaiting Landlord</span>;
    if (status === 'cancelled') return <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Cancelled</span>;
    return <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted">{status}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-6 sm:py-10 lg:py-12">
        <div className="mb-6 sm:mb-10">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight">
            {user?.role === 'tenant' ? 'My Applications & Leases' : 'Lease Agreements'}
          </h1>
          <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">
            {user?.role === 'landlord'
              ? 'Review and sign lease requests from tenants'
              : user?.role === 'tenant'
              ? 'Track the status of every application you\'ve submitted.'
              : 'View your lease agreements'}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 sm:py-20">Loading leases...</div>
        ) : leases.length === 0 ? (
          <Card className="p-10 sm:p-20 text-center border-2 border-dashed">
            <FileSignature className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg sm:text-xl font-medium mb-2">No Leases Yet</h3>
            <p className="text-muted-foreground text-sm sm:text-base">{user?.role === 'tenant' ? 'Browse properties and sign your first lease' : 'Tenants will send you lease requests here'}</p>
          </Card>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {leases.map((lease, idx) => (
              <Card key={lease.id} className="p-4 sm:p-6 border border-border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/leases/${lease.id}`)} data-testid={`lease-card-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-base sm:text-xl font-medium">{lease.property_title}</h3>
                      {getStatusBadge(lease.status, lease.payment_status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mt-3 sm:mt-4 text-xs sm:text-sm">
                      <div><div className="text-muted-foreground">Tenant</div><div className="font-medium truncate">{lease.tenant_name}</div></div>
                      <div><div className="text-muted-foreground">Landlord</div><div className="font-medium truncate">{lease.landlord_name}</div></div>
                      <div><div className="text-muted-foreground">Duration</div><div className="font-medium">{lease.lease_months} months</div></div>
                      <div><div className="text-muted-foreground">Monthly Rent</div><div className="font-medium text-primary">${lease.monthly_rent.toFixed(0)}</div></div>
                    </div>
                    {user?.role === 'tenant' && (
                      <ApplicationStepper status={lease.status} paymentStatus={lease.payment_status} />
                    )}
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full">View Lease</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
